import attackMap from "./attack-map.json";

// ─── Lookup tables (generated from MITRE STIX — see scripts/build-attack-map.mjs)

const TACTICS = Object.fromEntries(
  attackMap.tactics.map((t, i) => [t.id, { name: t.name, order: i }]),
);
const TECHNIQUES = attackMap.techniques;

// ─── Helpers ────────────────────────────────────────────────────────────────

/** "TA0007: Discovery" | "T1110.001: Brute Force: Password Guessing" | {id,name} → {id,name} */
function parse(raw) {
  const s = (typeof raw === "string" ? raw : raw?.id ?? "").trim();
  const m = s.match(/^((?:TA|T)\d{4}(?:\.\d{3})?)\s*:?\s*(.*)$/i);
  const id = (m ? m[1] : s).toUpperCase();
  const name =
    (typeof raw === "object" && raw?.name) || (m && m[2] ? m[2] : undefined);
  return { id, name };
}

const isTactic = (id) => /^TA\d{4}$/.test(id);
const isSub = (id) => /^T\d{4}\.\d{3}$/.test(id);
const parentOf = (subId) => subId.split(".")[0];
const techName = (id, fallback) => TECHNIQUES[id]?.name ?? fallback ?? id;
const techTactics = (id) => TECHNIQUES[id]?.tactics ?? [];

/**
 * Flatten whatever the backend sends into a single list of id strings.
 * Accepts the raw `mitre_attack` object ({tactics, techniques, ...}) or an array.
 */
export function toItems(mitreAttack) {
  if (Array.isArray(mitreAttack)) return mitreAttack;
  if (!mitreAttack || typeof mitreAttack !== "object") return [];
  return Object.values(mitreAttack)
    .filter(Array.isArray)
    .flat();
}

// ─── Main API ─────────────────────────────────────────────────────────────

/**
 * Turn a flat list (tactics and/or techniques and/or sub-techniques, any order)
 * into a kill-chain-ordered tree:
 *   [{ id, name, order, techniques: [{ id, name, present, tactics, subs: [...] }] }]
 */
export function groupMitre(input) {
  const items = toItems(input);
  const parsed = items.map(parse);

  const presentTactics = new Set(parsed.map((p) => p.id).filter(isTactic));
  const nameHint = new Map();
  const presentTech = new Set();
  const wantedTech = new Set();
  const wantedSubs = new Map();

  for (const p of parsed) {
    if (isTactic(p.id)) continue;
    if (p.name) nameHint.set(p.id, p.name);
    if (isSub(p.id)) {
      const parent = parentOf(p.id);
      wantedTech.add(parent);
      if (!wantedSubs.has(parent)) wantedSubs.set(parent, new Set());
      wantedSubs.get(parent).add(p.id);
    } else {
      presentTech.add(p.id);
      wantedTech.add(p.id);
    }
  }

  const tacticsForTechnique = (id) => {
    const mapped = techTactics(id);
    if (presentTactics.size === 0) return mapped;
    const hit = mapped.filter((t) => presentTactics.has(t));
    return hit.length ? hit : mapped;
  };

  const groups = new Map();
  const ensureGroup = (tacticId) => {
    const meta = TACTICS[tacticId];
    if (!meta) return null;
    if (!groups.has(tacticId)) {
      groups.set(tacticId, {
        id: tacticId,
        name: meta.name,
        order: meta.order,
        techniques: [],
      });
    }
    return groups.get(tacticId);
  };
  const ensureUnknown = () => {
    if (!groups.has("UNKNOWN")) {
      groups.set("UNKNOWN", {
        id: "UNKNOWN",
        name: "Unmapped",
        order: 999,
        techniques: [],
      });
    }
    return groups.get("UNKNOWN");
  };

  for (const t of presentTactics) ensureGroup(t);

  for (const techId of wantedTech) {
    const subs = [...(wantedSubs.get(techId) ?? [])].sort().map((sid) => ({
      id: sid,
      name: techName(sid, nameHint.get(sid)),
      tactics: techTactics(sid),
    }));

    const node = {
      id: techId,
      name: techName(techId, nameHint.get(techId)),
      present: presentTech.has(techId),
      tactics: techTactics(techId),
      subs,
    };

    const targets = tacticsForTechnique(techId);
    if (targets.length === 0) {
      ensureUnknown().techniques.push(node);
      continue;
    }
    for (const tacticId of targets) {
      const g = ensureGroup(tacticId);
      if (g) g.techniques.push(node);
    }
  }

  const out = [...groups.values()].sort((a, b) => a.order - b.order);
  for (const g of out) g.techniques.sort((a, b) => a.id.localeCompare(b.id));
  return out;
}

/**
 * "5 tactics · 7 techniques" — counts the actual mapped items (leaves), i.e.
 * sub-techniques plus techniques that have no sub-technique. Synthesized parent
 * rows (e.g. T1110 shown only to hold T1110.001) are not counted, so the number
 * matches what the backend actually sent.
 */
export function countLabel(groups) {
  const tactics = groups.length;
  const ids = new Set();
  for (const g of groups)
    for (const t of g.techniques) {
      if (t.subs.length === 0) ids.add(t.id);
      else for (const s of t.subs) ids.add(s.id);
    }
  const n = ids.size;
  return `${tactics} tactic${tactics === 1 ? "" : "s"} · ${n} technique${
    n === 1 ? "" : "s"
  }`;
}

/** attack.mitre.org URL for a tactic / technique / sub-technique id */
export function attackUrl(id) {
  if (id.startsWith("TA")) return `https://attack.mitre.org/tactics/${id}/`;
  return `https://attack.mitre.org/techniques/${id.replace(".", "/")}/`;
}